import { registerLocaleData } from '@angular/common'
import localeEnGb from '@angular/common/locales/en-GB'
import localeEs from '@angular/common/locales/es'
import localePt from '@angular/common/locales/pt'
import localeSv from '@angular/common/locales/sv'
import { inject, Injectable } from '@angular/core'
import { ICON, ICON_BY_DATAFLAG, ICON_PATH } from '@app/cnst/icons.cnst'
import { select, StoreService } from '@app/srv/store.service'
import { urlUtil } from '@app/util/url.util'
import { _mapValues } from '@naturalcycles/js-lib'
import { LANG, LANG_DEFAULT, toSupportedLanguageOrUndefined } from '@naturalcycles/shared'
import { TranslateService } from '@ngx-translate/core'
import { GRAPH_ICON_BY_DATAFLAG } from '@src/app/pages/graph/srv/graph.cnst.js'
import { lastValueFrom } from 'rxjs'
import { AccountService } from './account.service.js'
import { dispatch, getState } from './store.service.js'

// These functions are required for the angular/common DecimalPipe function
// TODO: Make our own implementation Decimal pipe and remove these functions
registerLocaleData(localeEnGb)
registerLocaleData(localeEs)
registerLocaleData(localePt)
registerLocaleData(localeSv)

@Injectable({ providedIn: 'root' })
export class LangService {
  private accountService = inject(AccountService)
  private storeService = inject(StoreService)
  private translateService = inject(TranslateService)

  private lang$ = select(s => s.account.lang as LANG)

  private _locale!: string
  public get locale(): string {
    return this._locale || LANG_DEFAULT
  }

  private set locale(locale: string) {
    this._locale = locale
  }

  public async init(): Promise<{ lang: LANG; locale: string }> {
    const { account, userLocale } = getState()
    const accountLang = account.lang
    const qsLang = urlUtil.getLocationQueryString()['lang']
    const browserLocale = this.getDeviceLanguage()
    const userCountry = userLocale.country

    const lang = this.detectLang(accountLang, qsLang, browserLocale)

    const locale = this.detectLocale(lang, browserLocale, userCountry)

    this.setLocale(lang, locale)

    this.lang$.subscribe(lang => {
      this.updateLocalizedIcons(lang)
    })

    await lastValueFrom(this.translateService.get('day'), { defaultValue: undefined }) // wait for translations to be ready

    document.documentElement.setAttribute('lang', lang)

    return { lang, locale }
  }

  public getDeviceLanguage(): string | undefined {
    return this.translateService.getBrowserCultureLang()
  }

  private detectLang(_accountLang?: string, _qsLang?: string, _browserLang?: string): LANG {
    const accountLang = toSupportedLanguageOrUndefined(_accountLang)
    const qsLang = toSupportedLanguageOrUndefined(_qsLang)
    const browserLang = toSupportedLanguageOrUndefined(_browserLang)

    return accountLang || qsLang || browserLang || LANG_DEFAULT
  }

  /**
   * Takes "left part" from the Language and "right part" from the country.
   * E.g lang=en-US plus country=SE will make it into `en-SE`.
   *
   * This is to be able to support locale-aware date formatting, while still keeping user's preferred Language.
   */
  private detectLocale(lang: LANG, browserLocale: string | undefined, country: string): string {
    const langPart = lang.slice(0, 2)

    // 2. Get locale from browser
    if (browserLocale?.includes('-')) {
      return `${langPart}-${browserLocale.split('-')[1]}`
    }

    // 3. Get locale from userLocale.country
    if (country) {
      return `${langPart}-${country}`
    }

    // 4. Use lang
    return lang
  }

  private setLocale(lang: LANG, locale: string): void {
    this.locale = locale

    this.translateService.use(lang)
    dispatch('setLocale', { lang, locale })
  }

  private updateLocalizedIcons(lang: LANG): void {
    _mapValues(ICON_BY_DATAFLAG, (_, value) => ICON_BY_LANG[lang]?.[value] || value, true)
    _mapValues(ICON, (_, value) => ICON_BY_LANG[lang]?.[value] || value, true)
    _mapValues(
      GRAPH_ICON_BY_DATAFLAG,
      (_, value) => {
        const icon = ICON_BY_LANG[lang]?.[value?.icon]

        if (icon) {
          return {
            ...value,
            icon,
          }
        }

        return value
      },
      true,
    )
  }

  public switchLanguage = async (lang: LANG): Promise<void> => {
    if (lang !== this.storeService.getState().account.lang) {
      await this.accountService.patch({ lang })
      await this.storeService.persistStateNowAndDisablePersistence()
    }
  }
}

const ICON_BY_LANG: Partial<Record<LANG, Partial<Record<ICON, string>>>> = {
  [LANG.es_US]: {
    [ICON.MOOD_PMS]: `${ICON_PATH}mood-pms-es.svg`,
  },
  [LANG.pt_BR]: {
    [ICON.MOOD_PMS]: `${ICON_PATH}mood-pms-pt.svg`,
  },
}
